import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useStudentNotes } from "@/hooks/useStudentNotes";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface NoteEditorProps {
  courseId?: string;
  lessonId?: string;
  moduleId?: string;
}

export const NoteEditor = ({ courseId, lessonId, moduleId }: NoteEditorProps) => {
  const [noteContent, setNoteContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { note, isLoading, saveNote } = useStudentNotes(courseId, lessonId, moduleId);
  
  // Only update content when the note data changes and content is empty
  useEffect(() => {
    if (note && note.content && noteContent === "") {
      setNoteContent(note.content);
    }
  }, [note, noteContent]);
  
  const handleNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContent(e.target.value);
  };
  
  const handleSave = async () => {
    if (!courseId) return;
    
    if (note && note.content === noteContent) {
      toast.info("No changes to save");
      return;
    }
    
    setIsSaving(true);
    try {
      await saveNote.mutateAsync({
        courseId,
        content: noteContent,
        lessonId,
        moduleId,
      });
      toast.success("Note saved successfully");
    } catch (error) {
      toast.error("Failed to save note");
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">My Notes</CardTitle>
        <CardDescription>
          Take notes for this {lessonId ? "lesson" : "module"}. Click Save to store your changes.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            <Textarea
              placeholder="Start taking notes..."
              className="min-h-[150px] resize-y"
              value={noteContent}
              onChange={handleNoteChange}
            />
            <div className="flex justify-end">
              <Button 
                size="sm" 
                onClick={handleSave}
                disabled={isSaving}
                variant="outline"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" /> Save
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
